import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ListChecks, Plus, X, Trash, Check, CalendarBlank, ArrowsClockwise,
  DotsThreeVertical, CircleNotch, PencilSimple, Kanban, ListDashes, Flag,
} from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * TasksPage — standalone task inbox for the current user.
 *
 * Two view modes: List (grouped by priority) and Board (kanban across
 * todo/doing/done). Full CRUD + subtasks + drag-to-status + quick create.
 *
 * Backed by /api/tasks. Tasks with `project_id` live inside their
 * respective project workspaces; this page shows my personal (project=None)
 * tasks only.
 */

const PRIORITY_STYLES = {
  urgent: { color: "#FF5D73", label: "Urgent" },
  high:   { color: "#FFD700", label: "High" },
  med:    { color: "#F2DC8C", label: "Medium" },
  low:    { color: "#AAB4C4", label: "Low" },
};

const STATUS_ORDER = ["todo", "doing", "done"];
const STATUS_LABEL = { todo: "To Do", doing: "In Progress", done: "Done" };

export default function TasksPage({ initialCreate = false, projectId, projectName }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board");   // "list" | "board"
  const [urlParams, setUrlParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(initialCreate || urlParams.get("new") === "1");
  const [editing, setEditing] = useState(null); // task obj or null

  // Clear the ?new=1 hint after we've consumed it so the modal doesn't
  // re-open on browser back.
  useEffect(() => {
    if (urlParams.get("new") === "1") {
      const next = new URLSearchParams(urlParams);
      next.delete("new");
      setUrlParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks", { params: projectId ? { project_id: projectId } : {} });
      setTasks(Array.isArray(data) ? data : []);
    } catch { setTasks([]); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const byStatus = useMemo(() => {
    const m = { todo: [], doing: [], done: [] };
    for (const t of tasks) m[t.status || "todo"]?.push(t);
    for (const s of STATUS_ORDER) m[s].sort((a, b) => (a.order || 0) - (b.order || 0));
    return m;
  }, [tasks]);

  const onDragEnd = async (res) => {
    const { destination, source, draggableId } = res;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const newStatus = destination.droppableId;
    // Compute new order value between neighbours.
    const col = byStatus[newStatus].filter((t) => t.task_id !== draggableId);
    const before = col[destination.index - 1];
    const after  = col[destination.index];
    let newOrder;
    if (!before && !after) newOrder = 10;
    else if (!before) newOrder = (after.order || 100) - 10;
    else if (!after)  newOrder = (before.order || 0) + 10;
    else newOrder = ((before.order || 0) + (after.order || 0)) / 2;
    // Optimistic UI.
    setTasks((prev) => prev.map((t) => t.task_id === draggableId
      ? { ...t, status: newStatus, order: newOrder } : t));
    try {
      if (res.source.droppableId !== newStatus) {
        await api.patch(`/tasks/${draggableId}/status`, { status: newStatus });
      }
      await api.patch(`/tasks/${draggableId}/order`, { order: newOrder });
    } catch { load(); }
  };

  const createTask = async (payload) => {
    const { data } = await api.post("/tasks", { ...payload, project_id: projectId || null });
    setTasks((p) => [data, ...p]);
    setCreateOpen(false);
  };
  const updateTask = async (tid, payload) => {
    const { data } = await api.put(`/tasks/${tid}`, payload);
    setTasks((p) => p.map((t) => t.task_id === tid ? data : t));
    setEditing(null);
  };
  const deleteTask = async (tid) => {
    await api.delete(`/tasks/${tid}`);
    setTasks((p) => p.filter((t) => t.task_id !== tid));
    setEditing(null);
  };
  const toggleStatus = async (tid, next) => {
    setTasks((p) => p.map((t) => t.task_id === tid ? { ...t, status: next } : t));
    try { await api.patch(`/tasks/${tid}/status`, { status: next }); } catch { load(); }
  };
  const setSubtasks = async (tid, subs) => {
    setTasks((p) => p.map((t) => t.task_id === tid ? { ...t, subtasks: subs } : t));
    try { await api.patch(`/tasks/${tid}/subtasks`, { subtasks: subs }); } catch { load(); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="tasks-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
            {projectName ? `${projectName} · Tasks` : "Command Center · Tasks"}
          </div>
          <h1 className="mt-1 font-display text-[34px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                letterSpacing: "0.02em",
              }}>
            {projectName ? "Board" : "Your Tasks"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle view={view} onChange={setView}/>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="tasks-new"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] transition-transform active:scale-95"
            style={{
              background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
              color: "#0a0703",
              boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
            }}>
            <Plus size={13} weight="bold"/> New Task
          </button>
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl p-10 text-center" data-testid="tasks-loading"
             style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
          <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)}/>
      ) : view === "board" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tasks-board">
            {STATUS_ORDER.map((s) => (
              <BoardColumn
                key={s}
                status={s}
                tasks={byStatus[s]}
                onEdit={setEditing}
                onToggle={toggleStatus}
              />
            ))}
          </div>
        </DragDropContext>
      ) : (
        <ListView tasks={tasks} onEdit={setEditing} onToggle={toggleStatus}/>
      )}

      <AnimatePresence>
        {(createOpen || editing) && (
          <TaskModal
            task={editing}
            onClose={() => { setCreateOpen(false); setEditing(null); }}
            onCreate={createTask}
            onUpdate={updateTask}
            onDelete={deleteTask}
            onSubtasks={setSubtasks}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function ViewToggle({ view, onChange }) {
  return (
    <div className="inline-flex rounded-xl p-1"
         data-testid="tasks-view-toggle"
         style={{ border: "1px solid rgba(212,175,55,0.5)", background: "rgba(0,0,0,0.4)" }}>
      {[{ k: "board", icon: Kanban }, { k: "list", icon: ListDashes }].map(({ k, icon: Icon }) => (
        <button key={k} onClick={() => onChange(k)}
                data-testid={`tasks-view-${k}`}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                style={view === k
                  ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                  : { color: "#F2DC8C" }}>
          <Icon size={14} weight={view === k ? "fill" : "regular"}/>
        </button>
      ))}
    </div>
  );
}


function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl p-10 text-center"
         data-testid="tasks-empty"
         style={{
           background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
           border: "1px solid rgba(212,175,55,0.45)",
         }}>
      <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
           style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
        <ListChecks size={22} weight="fill" color="#0a0703"/>
      </div>
      <div className="mt-4 font-display text-[24px] text-[#FFD700]">Clean slate.</div>
      <div className="mt-1 text-[13px] text-[#F2DC8C]/70">
        Create your first task to start owning the day.
      </div>
      <button
        onClick={onCreate}
        data-testid="tasks-empty-create"
        className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em]"
        style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
        <Plus size={12} weight="bold"/> New Task
      </button>
    </div>
  );
}


function BoardColumn({ status, tasks, onEdit, onToggle }) {
  return (
    <div className="rounded-2xl p-3 flex flex-col min-h-[300px]"
         data-testid={`board-col-${status}`}
         style={{
           background: "linear-gradient(180deg, rgba(26,18,6,0.6) 0%, rgba(5,3,0,0.85) 100%)",
           border: "1px solid rgba(212,175,55,0.35)",
         }}>
      <div className="flex items-center justify-between mb-2 px-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]">
          {STATUS_LABEL[status]}
        </div>
        <span className="text-[10px] tabular-nums font-display text-[#FFD700]">{tasks.length}</span>
      </div>
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
               className="flex-1 rounded-lg p-1 transition-colors"
               style={{ background: snapshot.isDraggingOver ? "rgba(255,215,0,0.05)" : "transparent" }}>
            {tasks.map((t, idx) => (
              <Draggable key={t.task_id} draggableId={t.task_id} index={idx}>
                {(prov, snap) => (
                  <div ref={prov.innerRef}
                       {...prov.draggableProps}
                       {...prov.dragHandleProps}
                       style={prov.draggableProps.style}
                       className="mb-2">
                    <TaskCard task={t} onEdit={onEdit} onToggle={onToggle} dragging={snap.isDragging}/>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}


function TaskCard({ task, onEdit, onToggle, dragging }) {
  const p = PRIORITY_STYLES[task.priority || "med"];
  const done = task.status === "done";
  const subs = task.subtasks || [];
  const subsDone = subs.filter((s) => s.done).length;
  return (
    <button
      type="button"
      onClick={() => onEdit(task)}
      data-testid={`task-card-${task.task_id}`}
      className="w-full text-left rounded-xl p-3 transition-all hover:-translate-y-0.5"
      style={{
        background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
        border: `1px solid ${dragging ? "#FFD700" : "rgba(212,175,55,0.45)"}`,
        boxShadow: dragging ? "0 10px 24px rgba(255,215,0,0.35)" : "0 3px 10px rgba(0,0,0,0.35)",
      }}>
      <div className="flex items-start gap-2">
        <span
          role="checkbox"
          aria-checked={done}
          onClick={(e) => { e.stopPropagation(); onToggle(task.task_id, done ? "todo" : "done"); }}
          data-testid={`task-toggle-${task.task_id}`}
          className="mt-0.5 w-4 h-4 rounded-md flex items-center justify-center shrink-0"
          style={{
            border: `1.5px solid ${done ? "#FFD700" : "rgba(212,175,55,0.55)"}`,
            background: done ? "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" : "transparent",
          }}>
          {done && <Check size={10} weight="bold" color="#0a0703"/>}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-[#F2DC8C] leading-snug break-words"
               style={{ textDecoration: done ? "line-through" : "none", opacity: done ? 0.55 : 1 }}>
            {task.title}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ border: `1px solid ${p.color}`, color: p.color }}>
              <Flag size={8} weight="fill"/> {p.label}
            </span>
            {task.due_at && (
              <span className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/70 inline-flex items-center gap-1">
                <CalendarBlank size={9}/> {new Date(task.due_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            )}
            {subs.length > 0 && (
              <span className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/60">
                {subsDone}/{subs.length}
              </span>
            )}
            {task.recurring && (
              <span className="text-[9px] uppercase tracking-widest text-[#FFD700] inline-flex items-center gap-1">
                <ArrowsClockwise size={9}/> {task.recurring}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}


function ListView({ tasks, onEdit, onToggle }) {
  // Group by priority; done tasks last.
  const grouped = useMemo(() => {
    const open = tasks.filter((t) => t.status !== "done");
    const done = tasks.filter((t) => t.status === "done");
    const byP = { urgent: [], high: [], med: [], low: [] };
    for (const t of open) byP[t.priority || "med"].push(t);
    return { byP, done };
  }, [tasks]);
  return (
    <div className="space-y-6" data-testid="tasks-list">
      {["urgent", "high", "med", "low"].map((k) => (
        grouped.byP[k].length > 0 && (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]/70 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rotate-45" style={{ background: PRIORITY_STYLES[k].color }}/>
              {PRIORITY_STYLES[k].label} · {grouped.byP[k].length}
            </div>
            <div className="space-y-2">
              {grouped.byP[k].map((t) => <TaskCard key={t.task_id} task={t} onEdit={onEdit} onToggle={onToggle}/>)}
            </div>
          </div>
        )
      ))}
      {grouped.done.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]/60 mb-2">
            Completed · {grouped.done.length}
          </div>
          <div className="space-y-2">
            {grouped.done.map((t) => <TaskCard key={t.task_id} task={t} onEdit={onEdit} onToggle={onToggle}/>)}
          </div>
        </div>
      )}
    </div>
  );
}


function TaskModal({ task, onClose, onCreate, onUpdate, onDelete, onSubtasks }) {
  const isEdit = !!task;
  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [priority, setPriority] = useState(task?.priority || "med");
  const [status, setStatus] = useState(task?.status || "todo");
  const [dueAt, setDueAt] = useState(task?.due_at ? task.due_at.slice(0, 16) : "");
  const [recurring, setRecurring] = useState(task?.recurring || "");
  const [subs, setSubs] = useState(task?.subtasks || []);
  const [subDraft, setSubDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        priority, status,
        due_at: dueAt || null,
        recurring: recurring || null,
        subtasks: subs,
      };
      if (isEdit) await onUpdate(task.task_id, payload);
      else        await onCreate(payload);
    } finally { setBusy(false); }
  };

  const addSub = () => {
    if (!subDraft.trim()) return;
    const next = [...subs, { text: subDraft.trim(), done: false }];
    setSubs(next);
    setSubDraft("");
    if (isEdit) onSubtasks?.(task.task_id, next);
  };
  const toggleSub = (i) => {
    const next = subs.map((s, j) => j === i ? { ...s, done: !s.done } : s);
    setSubs(next);
    if (isEdit) onSubtasks?.(task.task_id, next);
  };
  const removeSub = (i) => {
    const next = subs.filter((_, j) => j !== i);
    setSubs(next);
    if (isEdit) onSubtasks?.(task.task_id, next);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
      onClick={onClose}
      data-testid="task-modal">
      <motion.form
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
          border: "1.5px solid rgba(255,215,0,0.55)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.75), 0 0 40px rgba(255,215,0,0.18)",
          maxHeight: "88vh",
        }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          {isEdit ? <PencilSimple size={16} className="text-[#FFD700]"/> : <Plus size={16} weight="bold" className="text-[#FFD700]"/>}
          <div className="font-display text-[16px] text-[#FFD700]">{isEdit ? "Edit Task" : "New Task"}</div>
          <button type="button" onClick={onClose} data-testid="task-modal-close"
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: "68vh" }}>
          <Field label="Title">
            <input
              autoFocus
              data-testid="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 280))}
              placeholder="What needs doing?"
              className="w-full bg-transparent text-[15px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"
            />
          </Field>

          <Field label="Description (optional)">
            <textarea
              data-testid="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 8000))}
              rows={3}
              placeholder="Add context, links, or acceptance criteria…"
              className="w-full bg-transparent text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none resize-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <div className="flex gap-1.5 flex-wrap" data-testid="task-priorities">
                {Object.entries(PRIORITY_STYLES).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setPriority(k)}
                          data-testid={`task-priority-${k}`}
                          className="px-2 py-1 rounded-md text-[10px] uppercase tracking-widest transition-all"
                          style={priority === k
                            ? { background: v.color, color: "#0a0703" }
                            : { border: `1px solid ${v.color}`, color: v.color }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Status">
              <div className="flex gap-1.5" data-testid="task-statuses">
                {STATUS_ORDER.map((k) => (
                  <button key={k} type="button" onClick={() => setStatus(k)}
                          data-testid={`task-status-${k}`}
                          className="flex-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-widest transition-all"
                          style={status === k
                            ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                            : { border: "1px solid rgba(212,175,55,0.5)", color: "#F2DC8C" }}>
                    {STATUS_LABEL[k]}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date">
              <input
                data-testid="task-due"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                type="datetime-local"
                className="w-full bg-transparent text-[12px] text-[#F2DC8C] focus:outline-none"
                style={{ colorScheme: "dark" }}
              />
            </Field>
            <Field label="Recurring">
              <select
                data-testid="task-recurring"
                value={recurring}
                onChange={(e) => setRecurring(e.target.value)}
                className="w-full bg-transparent text-[12px] text-[#F2DC8C] focus:outline-none">
                <option value="" style={{ background: "#0a0703" }}>None</option>
                <option value="daily" style={{ background: "#0a0703" }}>Daily</option>
                <option value="weekly" style={{ background: "#0a0703" }}>Weekly</option>
                <option value="monthly" style={{ background: "#0a0703" }}>Monthly</option>
              </select>
            </Field>
          </div>

          <Field label={`Subtasks · ${subs.filter((s) => s.done).length}/${subs.length}`}>
            <div className="space-y-1.5" data-testid="task-subtasks">
              {subs.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button type="button" onClick={() => toggleSub(i)}
                          data-testid={`task-subtask-toggle-${i}`}
                          className="w-4 h-4 rounded flex items-center justify-center"
                          style={{
                            border: `1.5px solid ${s.done ? "#FFD700" : "rgba(212,175,55,0.55)"}`,
                            background: s.done ? "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" : "transparent",
                          }}>
                    {s.done && <Check size={9} weight="bold" color="#0a0703"/>}
                  </button>
                  <span className="flex-1 text-[12px] text-[#F2DC8C]"
                        style={{ textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.55 : 1 }}>
                    {s.text}
                  </span>
                  <button type="button" onClick={() => removeSub(i)}
                          data-testid={`task-subtask-remove-${i}`}
                          className="w-6 h-6 rounded flex items-center justify-center opacity-50 hover:opacity-100">
                    <X size={10} className="text-[#F2DC8C]"/>
                  </button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <input
                  data-testid="task-subtask-input"
                  value={subDraft}
                  onChange={(e) => setSubDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                  placeholder="Add subtask + Enter"
                  className="flex-1 bg-transparent text-[12px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none rounded px-2 py-1"
                  style={{ border: "1px solid rgba(212,175,55,0.35)" }}
                />
                <button type="button" onClick={addSub} data-testid="task-subtask-add"
                        className="px-2 rounded text-[10px] uppercase tracking-widest"
                        style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
                  Add
                </button>
              </div>
            </div>
          </Field>
        </div>

        <div className="flex items-center justify-between p-4 border-t"
             style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          {isEdit ? (
            <button type="button" onClick={() => onDelete(task.task_id)}
                    data-testid="task-delete"
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
              <Trash size={12}/> Delete
            </button>
          ) : <span/>}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} data-testid="task-cancel"
                    className="px-4 h-10 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
                    style={{ border: "1px solid rgba(212,175,55,0.45)" }}>
              Cancel
            </button>
            <button type="submit"
                    disabled={busy || !title.trim()}
                    data-testid="task-save"
                    className="px-4 h-10 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                    style={{
                      background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                      color: "#0a0703",
                      boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
                    }}>
              {busy ? "Saving…" : (isEdit ? "Save" : "Create Task")}
            </button>
          </div>
        </div>
      </motion.form>
    </motion.div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block rounded-xl p-3"
           style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">{label}</div>
      {children}
    </label>
  );
}
